import { useTranslation } from "react-i18next"

import { AnalyticsKpiTile } from "@/components/brand/analytics/analytics-kpi-tile"
import type { MissionAnalyticsOverviewKpis } from "@/types/mission-analytics"

/**
 * OKS-63 — Cross-mission overview KPI strip (4 tiles).
 *
 * 4 tiles in a `grid sm:grid-cols-2 lg:grid-cols-4` layout (UX Spec §5
 * mobile-first reflow — 2x2 on tablet/mobile, 4-col on desktop).
 *
 * Tiles render in fixed order:
 *   1. Total missions  (integer)
 *   2. Total collabs   (integer)
 *   3. Acceptance rate (percent + num/den tooltip)
 *   4. Completion rate (percent + num/den tooltip)
 */
export interface AnalyticsOverviewKpiStripProps {
  kpis: MissionAnalyticsOverviewKpis
}

export function AnalyticsOverviewKpiStrip({
  kpis,
}: AnalyticsOverviewKpiStripProps) {
  const { t } = useTranslation()

  return (
    <section
      aria-label={t("analytics.kpis.section_aria")}
      data-testid="analytics-overview-kpi-strip"
      className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <AnalyticsKpiTile
        labelKey="analytics.kpis.total_missions.label"
        ariaLabelKey="analytics.kpis.total_missions.aria"
        value={kpis.total_missions}
        kind="integer"
        testId="analytics-overview-kpi-tile-total_missions"
      />
      <AnalyticsKpiTile
        labelKey="analytics.kpis.total_collabs.label"
        ariaLabelKey="analytics.kpis.total_collabs.aria"
        value={kpis.total_collabs}
        kind="integer"
        testId="analytics-overview-kpi-tile-total_collabs"
      />
      <AnalyticsKpiTile
        labelKey="analytics.kpis.acceptance_rate.label"
        ariaLabelKey="analytics.kpis.acceptance_rate.aria"
        emptyAriaKey="analytics.kpis.acceptance_rate.empty_aria"
        value={kpis.acceptance_rate}
        kind="percent"
        numerator={kpis.acceptance_numerator}
        denominator={kpis.acceptance_denominator}
        tooltipKey="analytics.kpis.acceptance_rate.tooltip"
        testId="analytics-overview-kpi-tile-acceptance_rate"
      />
      <AnalyticsKpiTile
        labelKey="analytics.kpis.completion_rate.label"
        ariaLabelKey="analytics.kpis.completion_rate.aria"
        emptyAriaKey="analytics.kpis.completion_rate.empty_aria"
        value={kpis.completion_rate}
        kind="percent"
        numerator={kpis.completion_numerator}
        denominator={kpis.completion_denominator}
        tooltipKey="analytics.kpis.completion_rate.tooltip"
        testId="analytics-overview-kpi-tile-completion_rate"
      />
    </section>
  )
}
